type TabGroupProps<T extends string> = {
  tabs: readonly T[]
  value: T
  onChange: (tab: T) => void
  labels?: Partial<Record<T, string>>
  badges?: Partial<Record<T, number | undefined>>
  variant?: 'pill' | 'underline'
  size?: 'sm' | 'base'
  className?: string
}

/**
 * Reusable tab group component with consistent styling
 */
export function TabGroup<T extends string>({
  tabs,
  value,
  onChange,
  labels,
  badges,
  variant = 'pill',
  size = 'base',
  className = ''
}: TabGroupProps<T>) {
  const sizeClasses = size === 'sm'
    ? 'px-3 py-1 text-xs'
    : 'px-4 py-1.5 text-sm'

  if (variant === 'underline') {
    return (
      <div className={`flex gap-1 ${className}`}>
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => onChange(tab)}
            className={`${sizeClasses} font-medium rounded-md transition-all ${
              value === tab
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            {labels?.[tab] ?? tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
    )
  }

  // Default pill variant
  return (
    <div className={`flex gap-1 p-1  rounded-lg ${className}`}>
      {tabs.map(tab => {
        const badge = badges?.[tab]
        return (
          <button
            key={tab}
            onClick={() => onChange(tab)}
            className={`${sizeClasses} font-medium rounded-md transition-all flex items-center gap-1.5 ${
              value === tab
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {labels?.[tab] ?? tab.charAt(0).toUpperCase() + tab.slice(1)}
            {badge !== undefined && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full">
                {badge}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
